import React from 'react';
import { Card, CardContent, Typography, Box, Avatar, Chip, IconButton } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import './ActivityFeed.css';

const ActivityFeed = ({ activities = [], onRefresh }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'customer_added':
        return <PersonAddIcon sx={{ fontSize: 20 }} />;
      case 'employee_added':
        return <PersonAddIcon sx={{ fontSize: 20 }} />;
      case 'work_order_created':
        return <AssignmentIcon sx={{ fontSize: 20 }} />;
      case 'work_order_completed':
        return <CheckCircleIcon sx={{ fontSize: 20 }} />;
      default:
        return <AssignmentIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'customer_added':
        return '#3B82F6';
      case 'employee_added':
        return '#10B981';
      case 'work_order_created':
        return '#F59E0B';
      case 'work_order_completed':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Date unknown';
    return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const displayActivities = activities;

  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: '2px solid #e0e0e0',
        boxShadow: 'none',
        height: '100%'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#002244',
              fontWeight: 600,
              fontSize: '1.1rem'
            }}
          >
            Recent Activity
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onRefresh && (
              <IconButton
                onClick={onRefresh}
                size="small"
                sx={{
                  border: '1px solid #e0e0e0',
                  width: 28,
                  height: 28,
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            <Chip
              label={`${displayActivities.length} items`}
              size="small"
              sx={{
                backgroundColor: '#E8F4F8',
                color: '#002244',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {displayActivities.map((activity, index) => (
            <Box
              key={activity.id || index}
              className="activity-item"
              sx={{
                display: 'flex',
                gap: 2,
                padding: '12px',
                borderRadius: '12px',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#e9ecef',
                  transform: 'translateX(4px)'
                }
              }}
            >
              <Avatar
                sx={{
                  backgroundColor: `${getActivityColor(activity.type)}15`,
                  color: getActivityColor(activity.type),
                  width: 40,
                  height: 40
                }}
              >
                {getActivityIcon(activity.type)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#002244',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    mb: 0.5
                  }}
                >
                  {activity.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '0.8rem',
                    display: 'block',
                    mb: 0.5
                  }}
                >
                  {activity.description}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999',
                    fontSize: '0.75rem'
                  }}
                >
                  {formatTime(activity.timestamp)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {displayActivities.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: '#999'
            }}
          >
            <Typography variant="body2">No recent activity</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
